import { Link } from "react-router-dom";
import IntranetLayout from "@/components/IntranetLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, ShieldCheck, FileSignature, Calculator, TrendingUp, ArrowRight, Briefcase } from "lucide-react";
import AIManagerPanel from "@/components/ai/AIManagerPanel";

const ToolCard = ({ icon: Icon, title, desc, to }: any) => (
  <Card className="border-[#D0DDE6] hover:border-[#288CC8] transition-colors">
    <CardContent className="p-5 space-y-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#288CC8]/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#288CC8]" />
        </div>
        <h3 className="font-bold text-[#00283C]">{title}</h3>
      </div>
      <p className="text-xs text-muted-foreground">{desc}</p>
      <Button asChild size="sm" variant="outline" className="w-full">
        <Link to={to}>Open <ArrowRight className="w-3 h-3 ml-1" /></Link>
      </Button>
    </CardContent>
  </Card>
);

const CommercialHub = () => {
  return (
    <IntranetLayout>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-[#00283C] font-display">Commercial Hub</h1>
        <p className="text-sm text-muted-foreground">Vessel Vetting AI, Proposal Engine, Hire Calculator and Rate Intelligence — orchestrated by the Vel-iO Commercial Director.</p>
      </div>

      <Tabs defaultValue="ai" className="w-full">
        <TabsList className="bg-[#F4F7FA] mb-4">
          <TabsTrigger value="ai"><Sparkles className="w-4 h-4 mr-1" /> AI Director</TabsTrigger>
          <TabsTrigger value="tools"><Briefcase className="w-4 h-4 mr-1" /> Commercial Tools</TabsTrigger>
        </TabsList>

        <TabsContent value="ai" className="mt-0">
          <AIManagerPanel
            hub="commercial"
            title="Vel-iO Commercial Director"
            subtitle="I score tenders, recommend the right vessel for each RFP, draft proposals and monitor market rates — all gated by your approval."
            accent="text-[#E8A020]"
            context="Hub: Commercial. Tools available: Vessel Vetting AI (technical fit), Proposal Engine (proposal drafting), Hire Calculator (day-rate modelling), Rate Intelligence (market rates). Fleet: AIRCAT 35 Crewliner & Combi vessels operating West Africa, GoM, Guyana. Active tenders monitored via Fleet Intelligence."
          />
        </TabsContent>

        <TabsContent value="tools" className="mt-0">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ToolCard icon={ShieldCheck} title="Vessel Vetting AI" desc="OCIMF/OVMSA-aligned vessel screening and technical scoring against client RFPs." to="/employee/vessel-vetting-ai" />
            <ToolCard icon={FileSignature} title="Proposal Engine" desc="Draft client-ready commercial proposals from RFP intake to executive cover letter." to="/employee/proposals" />
            <ToolCard icon={Calculator} title="Hire Calculator" desc="Model day-rates, mob/demob, fuel and crewing across vessel classes and contract durations." to="/employee/hire-calculator" />
            <ToolCard icon={TrendingUp} title="Rate Intelligence" desc="Live market rates and benchmarking across CTV/FCS classes and regions." to="/employee/rate-intelligence" />
            <ToolCard icon={Calculator} title="Crew Transfer Intelligence" desc="Helicopter vs CTV fuel & cost comparator with live Jet A-1 / MGO pricing." to="/employee/crew-transfer-intelligence" />
            <ToolCard icon={Briefcase} title="Contracts" desc="Active charter parties, key dates and counterparty contacts." to="/employee/contracts" />
            <ToolCard icon={FileSignature} title="Contract Redline" desc="AI-assisted contract redlining and clause comparison." to="/employee/contract-redline" />
          </div>
        </TabsContent>
      </Tabs>
    </IntranetLayout>
  );
};

export default CommercialHub;
